"use client";

import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { db } from "@/lib/db";
import type { AiProfile } from "@/types/ai";
import { useLoadingData } from "@/lib/hooks/useLoadingData";
import SettingsManager from "@/components/settings/SettingsManager";
import BrandStyles from "@/components/branding/BrandStyles";
import PremiumHeroBackground from "@/components/ui/PremiumHeroBackground";
import UltraNavigationBarNavOnly from "@/components/chat/UltraNavigationBarNavOnly";

// Prevent static generation - uses client-side database
export const dynamic = "force-dynamic";

async function loadSettingsProfile(): Promise<AiProfile | null> {
  const profiles = await db.aiProfiles.orderBy("createdAt").reverse().limit(1).toArray();
  return profiles[0] ?? null;
}

export default function SettingsPage() {
  const router = useRouter();
  const { data: profile, loading, error, reload } = useLoadingData(loadSettingsProfile);

  useEffect(() => {
    if (loading || profile) return;
    if (error) {
      console.error("Failed to load profile:", error);
    }
    router.push("/onboarding");
  }, [loading, profile, error, router]);

  if (loading) {
    return (
      <PremiumHeroBackground className="min-h-screen flex flex-col items-center justify-center gap-4">
        <div className="w-8 h-8 border-2 border-white/60 border-t-transparent rounded-full animate-spin" aria-hidden />
        <p className="text-white/90 text-sm">Loading settings...</p>
      </PremiumHeroBackground>
    );
  }

  if (!profile) {
    return null;
  }

  return (
    <PremiumHeroBackground className="min-h-screen">
      <BrandStyles brand={profile.brand} />
      <SettingsManager profile={profile} onProfileUpdate={() => reload()} />
      {/* Ultra Navigation Bar - Navigation Only (no input needed on settings) */}
      <UltraNavigationBarNavOnly profile={profile} />
    </PremiumHeroBackground>
  );
}

